/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { formatDistanceToNow } from 'date-fns';

import { formatStoredDateTime } from '../../utils/datetime';

import type {
  AISystemExecution,
  AISystemExecutionListItem,
  ActionsSummary,
  ExecutionStatus,
} from '../../types';

type StatusTone = 'default' | 'success' | 'error' | 'warning' | 'info';

type RunLike = Pick<
  AISystemExecutionListItem,
  'status' | 'status_display' | 'created_at' | 'completed_at' | 'execution_time_ms' | 'actions_summary'
>;

type CreditUsageLike = {
  credit_usage?: number | null;
  credits_used?: number | null;
  billing_amount?: number | null;
  billing_units?: string | null;
  metadata?: Record<string, any> | null;
};

const normalizeStatus = (status: string): string => status.toUpperCase();

export const getStatusTone = (status: string): StatusTone => {
  switch (normalizeStatus(status)) {
    case 'COMPLETED':
    case 'SUCCESS':
    case 'CACHED':
      return 'success';
    case 'ERROR':
    case 'FAILED':
    case 'STOPPED':
    case 'CANCELLED':
      return 'error';
    case 'RUNNING':
      return 'info';
    case 'PENDING':
    case 'START':
      return 'warning';
    default:
      return 'default';
  }
};

export const getStatusLabel = (status: ExecutionStatus, statusDisplay?: string): string => {
  if (statusDisplay) {
    return statusDisplay;
  }

  switch (normalizeStatus(status)) {
    case 'COMPLETED':
    case 'SUCCESS':
      return 'Completed';
    case 'ERROR':
    case 'FAILED':
      return 'Failed';
    case 'RUNNING':
      return 'Running';
    case 'PENDING':
    case 'START':
      return 'Pending';
    case 'CACHED':
      return 'Cached';
    default:
      return String(status);
  }
};

export const formatDurationMs = (durationMs: number | null | undefined): string => {
  if (durationMs == null) {
    return '-';
  }

  if (durationMs >= 60000) {
    return `${(durationMs / 60000).toFixed(1)}m`;
  }

  if (durationMs >= 1000) {
    return `${(durationMs / 1000).toFixed(2)}s`;
  }

  return `${durationMs.toFixed(0)}ms`;
};

export const formatRunDuration = (run: Pick<RunLike, 'execution_time_ms' | 'created_at' | 'completed_at'>): string => {
  if (run.execution_time_ms != null) {
    return formatDurationMs(run.execution_time_ms);
  }

  if (!run.completed_at) {
    return 'In progress';
  }

  const durationMs = new Date(run.completed_at).getTime() - new Date(run.created_at).getTime();
  return formatDurationMs(durationMs);
};

export const formatCredits = (
  value: number | null | undefined,
  options: { compact?: boolean; emptyLabel?: string } = {},
): string => {
  const { compact = false, emptyLabel = '-' } = options;

  if (value == null || !Number.isFinite(Number(value))) {
    return emptyLabel;
  }

  const normalized = Number(value);
  if (compact) {
    if (normalized === 0) {
      return '0 cr';
    }
    if (Math.abs(normalized) < 0.0001) {
      return '<0.0001 cr';
    }
    return `${normalized.toLocaleString(undefined, {
      minimumFractionDigits: normalized < 10 ? 2 : 0,
      maximumFractionDigits: normalized < 10 ? 4 : 2,
    })} cr`;
  }

  return `${normalized.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })} credits`;
};

const firstFiniteCreditValue = (...values: Array<unknown>): number | null => {
  for (const value of values) {
    if (value == null || value === '') {
      continue;
    }
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric >= 0) {
      return numeric;
    }
  }
  return null;
};

export const getExecutionCreditUsage = (execution: CreditUsageLike): number | null => {
  const metadata = execution.metadata || {};
  const billingEstimate =
    metadata && typeof metadata.billing_estimate === 'object' && metadata.billing_estimate
      ? metadata.billing_estimate
      : {};
  const settlement =
    metadata && typeof metadata.billing_settlement === 'object' && metadata.billing_settlement
      ? metadata.billing_settlement
      : {};

  return firstFiniteCreditValue(
    execution.credit_usage,
    execution.credits_used,
    execution.billing_amount,
    metadata.billing_amount,
    metadata.credits_used,
    metadata.credit_usage,
    settlement.transaction_amount,
    settlement.billed_credits,
    billingEstimate.billed_credits,
    billingEstimate.estimated_credits,
  );
};

export const formatMemory = (bytes: number | null | undefined): string => {
  if (bytes == null || bytes <= 0) {
    return '-';
  }

  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${bytes} B`;
};

export const formatTimestamp = (value: string | null | undefined): string => {
  if (!value) {
    return '-';
  }

  return formatStoredDateTime(value, undefined, '-');
};

export const formatRelativeTimestamp = (value: string): string => {
  return formatDistanceToNow(new Date(value), { addSuffix: true });
};

export const getActionsSummary = (run: { actions_summary: ActionsSummary | null }): ActionsSummary | null => {
  if (!run.actions_summary || run.actions_summary.total === 0) {
    return null;
  }

  return run.actions_summary;
};

export const getRunOutcomeSummary = (run: RunLike): { tone: StatusTone; label: string; detail: string } => {
  const actionsSummary = getActionsSummary(run);
  if (actionsSummary) {
    if (actionsSummary.failed > 0) {
      return {
        tone: actionsSummary.successful > 0 ? 'warning' : 'error',
        label: `${actionsSummary.successful}/${actionsSummary.total} actions succeeded`,
        detail:
          actionsSummary.failed === 1
            ? '1 action failed during the run'
            : `${actionsSummary.failed} actions failed during the run`,
      };
    }

    return {
      tone: 'success',
      label: `${actionsSummary.total} action${actionsSummary.total === 1 ? '' : 's'} completed`,
      detail: 'Run produced downstream actions',
    };
  }

  switch (normalizeStatus(run.status)) {
    case 'RUNNING':
    case 'PENDING':
    case 'START':
      return {
        tone: 'info',
        label: 'In progress',
        detail: 'Run is still processing',
      };
    case 'ERROR':
    case 'FAILED':
    case 'STOPPED':
    case 'CANCELLED':
      return {
        tone: 'error',
        label: 'Needs attention',
        detail: 'Run ended before completing cleanly',
      };
    default:
      return {
        tone: 'default',
        label: 'No actions taken',
        detail: 'Run completed without downstream changes',
      };
  }
};

export const getExecutionTotals = (execution: AISystemExecution): {
  creditUsage: number | null;
  totalTokens: number;
} => {
  const totalTokens =
    execution.total_tokens ?? execution.expert_results.reduce((sum, result) => sum + (result.total_token || 0), 0);

  return {
    creditUsage: getExecutionCreditUsage(execution),
    totalTokens,
  };
};

export const getCompletedExpertsCount = (execution: AISystemExecution): number => {
  return execution.expert_results.filter((result) => {
    const status = normalizeStatus(result.status);
    return status === 'SUCCESS' || status === 'COMPLETED' || status === 'CACHED';
  }).length;
};
